/**
 * @fileoverview Knowledge Base Assignment Module - Configurable assignment component
 * 
 * This module provides a reusable knowledge base assignment interface that can be configured
 * for different contexts (user dashboard, parrot creation/editing, admin).
 * 
 * Requirements: 4.1, 4.2, 9.1, 9.2
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useModuleContext, useModuleEvents } from '../context/ModuleContextProvider.jsx';
import { ModuleEventType } from '../types/index.js';
import { 
  validateKnowledgeBaseAssignmentConfig, 
  mergeWithDefaults,
  ASSIGNMENT_STATUS_CONFIG,
  SYNC_STATUS_CONFIG
} from './types.js';
import { AssignmentInterface } from './components/AssignmentInterface.jsx';
import { BatchOperationsPanel } from './components/BatchOperationsPanel.jsx';
import { SyncStatusDisplay } from './components/SyncStatusDisplay.jsx';
import { ConflictResolutionDialog } from './components/ConflictResolutionDialog.jsx';
import { AssignmentHistoryPanel } from './components/AssignmentHistoryPanel.jsx';
import { useAssignmentManager } from './hooks/useAssignmentManager.js';
import { useAssignmentSecurity } from './hooks/useAssignmentSecurity.js';
import { useAssignmentValidation } from './hooks/useAssignmentValidation.js';
import { useSyncStatusTracking } from './hooks/useSyncStatusTracking.js';
import { assignmentService } from './services/AssignmentService.js';

/**
 * Knowledge Base Assignment Module Component
 * 
 * @param {Object} props - Component props
 * @param {KnowledgeBaseAssignmentConfig} props.config - Module configuration
 * @param {ModuleContext} props.context - Module context
 * @param {function(ModuleEvent): void} [props.onEvent] - Event handler
 * @param {string} [props.className] - CSS class name
 * @param {string} [props.testId] - Test ID
 */
const KnowledgeBaseAssignmentModule = ({ 
  config: userConfig, 
  context, 
  onEvent, 
  className='', 
  testId = 'kb-assignment-module' 
}) => {
  // Merge user config with defaults
  const config = useMemo(() => {
    return mergeWithDefaults(userConfig, userConfig?.context);
  }, [userConfig]);

  // Validate configuration
  const configValidation = validateKnowledgeBaseAssignmentConfig(config);
  if (!configValidation.valid) {
    throw new Error(`Invalid KnowledgeBaseAssignment configuration: ${configValidation.errors.join(', ')}`);
  }

  // Module context and events
  const moduleContext = useModuleContext();
  const { emit: emitEvent } = useModuleEvents('*', null, { enabled: false });

  // Module state
  const [moduleState, setModuleState] = useState({
    initialized: false,
    loading: false,
    error: null,
    view: 'assignments' // 'assignments' | 'history' | 'conflicts'
  });

  // Data state
  const [dataState, setDataState] = useState({
    files: [],
    parrots: [],
    assignments: {},
    syncStatuses: {},
    conflicts: [],
    batchOperations: []
  });

  // UI state
  const [uiState, setUiState] = useState({
    selectedFiles: new Set(),
    selectedParrots: new Set(),
    expandedFiles: new Set(),
    showConflictDialog: false,
    showHistoryPanel: false,
    filterOptions: {
      status: 'all',
      parrot: 'all',
      syncStatus: 'all'
    }
  });

  // Custom hooks for functionality
  const security = useAssignmentSecurity(config, moduleContext);
  const validation = useAssignmentValidation(config);
  const assignmentManager = useAssignmentManager(config, moduleContext);
  const syncTracking = useSyncStatusTracking(config, moduleContext);

  // Initialize module
  useEffect(() => {
    const initializeModule = async () => {
      try {
        setModuleState(prev => ({ ...prev, loading: true, error: null }));

        // Wait for module context to be initialized
        if (!moduleContext.initialized) {
          setModuleState(prev => ({ ...prev, loading: false }));
          return;
        }

        // Validate security context with better error handling
        try {
          await security.validateContext();
        } catch (securityError) {
          // For user contexts, we might want to be more lenient
          if (config.context === 'user-kb-dashboard' && securityError.message.includes('permission')) {
            // Proceeding with limited permissions for user context
          } else {
            throw securityError;
          }
        }
        
        // Load initial data
        await loadInitialData();
        
        setModuleState(prev => ({ 
          ...prev, 
          initialized: true, 
          loading: false,
          error: null 
        }));
        
        // Emit module loaded event
        emitEvent({
          type: ModuleEventType.MODULE_LOADED,
          payload: {
            moduleId: 'knowledge-base-assignment-module',
            context: config.context,
            securityLevel: config.securityLevel
          },
          source: 'knowledge-base-assignment-module',
          timestamp: Date.now()
        });

      } catch (error) {
        setModuleState(prev => ({ 
          ...prev, 
          initialized: false, 
          loading: false,
          error: error.message 
        }));
      }
    };

    // Only initialize when module context is ready
    if (moduleContext.initialized) {
      initializeModule();
    }
  }, [
    // Depend only on stable primitives/callbacks to avoid re-running every render
    config.context,
    config.securityLevel,
    security.validateContext,
    emitEvent,
    moduleContext.initialized
  ]);

  // Load initial data (files, parrots, assignments)
  const loadInitialData = useCallback(async () => {
    try {
      const [filesData, parrotsData, assignmentsData] = await Promise.all([
        loadFiles(),
        loadParrots(),
        loadAssignments()
      ]);

      setDataState(prev => ({
        ...prev,
        files: filesData,
        parrots: parrotsData,
        assignments: assignmentsData.assignments,
        syncStatuses: assignmentsData.syncStatuses
      }));

      // Initialize sync status tracking if enabled
      if (config.enableRealTimeUpdates) {
        syncTracking.initialize(filesData.map(f => f.id));
      }

    } catch (error) {
      throw error;
    }
  }, [config.enableRealTimeUpdates, syncTracking]);

  // Load knowledge base files
  const loadFiles = useCallback(async () => {
    try {
      const response = await moduleContext.apiClient.get('/api/knowledge-base', {
        context: config.context,
        securityLevel: config.securityLevel
      });
      // Knowledge base files loaded successfully
      return response.files || response.data || response.documents || response || [];
    } catch (error) {
      return [];
    }
  }, [moduleContext.apiClient, config.context, config.securityLevel]);

  // Load parrots
  const loadParrots = useCallback(async () => {
    try {
      const response = await moduleContext.apiClient.get('/api/knowledge-base/parrots', {
        context: config.context,
        securityLevel: config.securityLevel
      });
      // Parrots loaded successfully
      return response.parrots || response.data || response || [];
    } catch (error) {
      return [];
    }
  }, [moduleContext.apiClient, config.context, config.securityLevel]);

  // Load assignments and sync statuses
  const loadAssignments = useCallback(async () => {
    try {
      // Get all knowledge base files which now include assignment info
      const kbResponse = await moduleContext.apiClient.get('/api/knowledge-base', {
        context: config.context,
        securityLevel: config.securityLevel
      });
      
      const assignments = {};
      const syncStatuses = {};
      
      // Process files and their assignments
      const files = kbResponse.data || kbResponse.documents || kbResponse || [];
      
      for (const file of files) {
        if (file.assignments && file.assignments.length > 0) {
          // Convert assignment format to match expected structure
          assignments[file.id] = file.assignments.map(assignment => ({
            parrotId: assignment.parrotId,
            parrotName: assignment.parrotName,
            status: 'assigned', // Default status
            assignedAt: assignment.assignedAt
          }));
          
          // Initialize sync statuses based on file sync status
          file.assignments.forEach(assignment => {
            if (!syncStatuses[file.id]) {
              syncStatuses[file.id] = {};
            }
            // Use file-level sync status as default
            syncStatuses[file.id][assignment.parrotId] = {
              status: file.sync_status || 'pending',
              syncedAt: file.updated_at,
              elevenLabsDocumentId: file.elevenlabs_document_id
            };
          });
        }
      }
      
      // Assignments loaded successfully
      return { assignments, syncStatuses };
    } catch (error) {
      return { assignments: {}, syncStatuses: {} };
    }
  }, [moduleContext.apiClient, config.context, config.securityLevel]);

  // Handle assignment toggle with ElevenLabs sync integration
  const handleAssignmentToggle = useCallback(async (fileId, parrotId, currentlyAssigned) => {
    try {
      // Update local state to show syncing status immediately
      setDataState(prev => ({
        ...prev,
        syncStatuses: {
          ...prev.syncStatuses,
          [fileId]: {
            ...prev.syncStatuses[fileId],
            [parrotId]: {
              status: 'syncing',
              syncedAt: new Date().toISOString()
            }
          }
        }
      }));

      // Call the assignment API
      const operation = currentlyAssigned ? 'unassign' : 'assign';
      const response = await moduleContext.apiClient.post('/api/knowledge-base/assignments', {
        fileIds: [fileId],
        parrotIds: [parrotId],
        operation: operation
      });

      if (response.success) {
        // Update local sync status to pending (will be updated by queue processing)
        setDataState(prev => ({
          ...prev,
          syncStatuses: {
            ...prev.syncStatuses,
            [fileId]: {
              ...prev.syncStatuses[fileId],
              [parrotId]: {
                status: 'pending',
                syncedAt: new Date().toISOString()
              }
            }
          }
        }));

        // Reload assignments to get updated data
        const assignmentsData = await loadAssignments();
        setDataState(prev => ({
          ...prev,
          assignments: assignmentsData.assignments,
          syncStatuses: {
            ...prev.syncStatuses,
            ...assignmentsData.syncStatuses
          }
        }));

        // Emit assignment change event
        emitEvent({
          type: ModuleEventType.MODULE_DATA_CHANGED,
          payload: {
            moduleId: 'knowledge-base-assignment-module',
            operation: operation,
            fileId,
            parrotId,
            result: response
          },
          source: 'knowledge-base-assignment-module',
          timestamp: Date.now()
        });

        // Call config callback if provided
        if (config.onAssignmentChange) {
          config.onAssignmentChange(Object.values(assignmentsData.assignments).flat());
        }

        moduleContext.toast.success(`File ${operation === 'assign' ? 'assigned to' : 'unassigned from'} parrot successfully`);

      } else {
        throw new Error(response.error?.message || 'Assignment operation failed');
      }

    } catch (error) {
      console.error('Assignment toggle error:', error);
      
      // Revert sync status on error
      setDataState(prev => ({
        ...prev,
        syncStatuses: {
          ...prev.syncStatuses,
          [fileId]: {
            ...prev.syncStatuses[fileId],
            [parrotId]: {
              status: 'failed',
              error: error.message,
              lastAttempt: new Date().toISOString()
            }
          }
        }
      }));

      moduleContext.toast.error(`Assignment failed: ${error.message}`);
      
      // Emit error event
      emitEvent({
        type: ModuleEventType.MODULE_ERROR,
        payload: {
          moduleId: 'knowledge-base-assignment-module',
          operation: currentlyAssigned ? 'unassign' : 'assign',
          fileId,
          parrotId,
          error: error.message
        },
        source: 'knowledge-base-assignment-module',
        timestamp: Date.now()
      });
    }
  }, [config, emitEvent, loadAssignments, moduleContext.toast, moduleContext.apiClient]);

  // Handle conflict checking for batch operations
  const handleConflictCheck = useCallback(async (operation, files, parrots) => {
    try {
      const operations = [];
      files.forEach(file => {
        parrots.forEach(parrot => {
          operations.push({
            fileId: file.id,
            parrotId: parrot.id,
            operation: operation === 'unassign' ? 'unassign' : 'assign'
          });
        });
      });

      return await assignmentManager.checkConflicts(operations);
    } catch (error) {
      return [];
    }
  }, [assignmentManager]);

  // Handle batch assignment
  const handleBatchAssignment = useCallback(async (operation, fileIds, parrotIds) => {
    try {
      if (!config.allowBatchOperations) {
        throw new Error('Batch operations are not allowed in this context');
      }

      // Call the batch assignment API
      const response = await moduleContext.apiClient.post('/api/knowledge-base/assignments', {
        fileIds: fileIds,
        parrotIds: parrotIds,
        operation: operation
      });

      if (response.success) {
        // Reload assignments to get updated data
        const assignmentsData = await loadAssignments();
        setDataState(prev => ({
          ...prev,
          assignments: assignmentsData.assignments,
          syncStatuses: assignmentsData.syncStatuses
        }));

        const summary = response.summary || {};
        moduleContext.toast.success(
          `Batch ${operation} completed: ${summary.successful || 0} successful, ${summary.skipped || 0} skipped, ${summary.errors || 0} errors`
        );

        // Call config callback if provided
        if (config.onAssignmentChange) {
          config.onAssignmentChange(Object.values(assignmentsData.assignments).flat());
        }

      } else {
        throw new Error(response.error?.message || 'Batch operation failed');
      }

    } catch (error) {
      console.error('Batch assignment error:', error);
      moduleContext.toast.error(`Batch operation failed: ${error.message}`);
    }
  }, [config.allowBatchOperations, loadAssignments, moduleContext.toast, moduleContext.apiClient, config.onAssignmentChange]);

  // Handle conflict resolution
  const handleConflictResolution = useCallback(async (resolutions) => {
    try {
      await assignmentManager.resolveConflicts(dataState.conflicts, resolutions);
      
      // Clear conflicts and reload data
      setDataState(prev => ({ ...prev, conflicts: [] }));
      setUiState(prev => ({ ...prev, showConflictDialog: false }));
      
      const assignmentsData = await loadAssignments();
      setDataState(prev => ({
        ...prev,
        assignments: assignmentsData.assignments,
        syncStatuses: assignmentsData.syncStatuses
      }));

      moduleContext.toast.success('Conflicts resolved successfully');

    } catch (error) {
      moduleContext.toast.error(`Conflict resolution failed: ${error.message}`);
    }
  }, [assignmentManager, dataState.conflicts, loadAssignments, moduleContext.toast]);

  // Handle file selection for batch operations
  const handleFileSelection = useCallback((fileId, selected) => {
    setUiState(prev => ({
      ...prev,
      selectedFiles: selected 
        ? new Set([...prev.selectedFiles, fileId])
        : new Set([...prev.selectedFiles].filter(id => id !== fileId))
    }));
  }, []);

  // Handle parrot selection for batch operations
  const handleParrotSelection = useCallback((parrotId, selected) => {
    setUiState(prev => ({
      ...prev,
      selectedParrots: selected 
        ? new Set([...prev.selectedParrots, parrotId])
        : new Set([...prev.selectedParrots].filter(id => id !== parrotId))
    }));
  }, []);

  // Handle file expansion toggle
  const handleFileExpansion = useCallback((fileId) => {
    setUiState(prev => ({
      ...prev,
      expandedFiles: prev.expandedFiles.has(fileId)
        ? new Set([...prev.expandedFiles].filter(id => id !== fileId))
        : new Set([...prev.expandedFiles, fileId])
    }));
  }, []);

  // Get effective assignment status (considering pending changes)
  const getAssignmentStatus = useCallback((fileId, parrotId) => {
    const fileAssignments = dataState.assignments[fileId] || [];
    const assignment = fileAssignments.find(a => a.parrotId === parrotId);
    return assignment ? assignment.status : null;
  }, [dataState.assignments]);

  // Get sync status with real-time updates
  const getSyncStatus = useCallback((fileId, parrotId) => {
    // Try real-time status first if enabled
    if (config.enableRealTimeUpdates) {
      const rtStatus = syncTracking.getStatus(fileId, parrotId);
      if (rtStatus) {
        return rtStatus;
      }
    }
    
    // Fall back to cached status
    return dataState.syncStatuses[fileId]?.[parrotId] || null;
  }, [config.enableRealTimeUpdates, syncTracking, dataState.syncStatuses]);

  // Filter assignments based on UI filters
  const filteredFiles = useMemo(() => {
    return dataState.files.filter(file => {
      const fileAssignments = dataState.assignments[file.id] || [];
      
      // Filter by status
      if (uiState.filterOptions.status !== 'all') {
        const hasStatus = fileAssignments.some(a => a.status === uiState.filterOptions.status);
        if (!hasStatus) return false;
      }
      
      // Filter by parrot
      if (uiState.filterOptions.parrot !== 'all') {
        const hasParrot = fileAssignments.some(a => a.parrotId === uiState.filterOptions.parrot);
        if (!hasParrot) return false;
      }
      
      // Filter by sync status
      if (uiState.filterOptions.syncStatus !== 'all') {
        const syncStatuses = dataState.syncStatuses[file.id] || {};
        const hasSyncStatus = Object.values(syncStatuses).some(s => s.status === uiState.filterOptions.syncStatus);
        if (!hasSyncStatus) return false;
      }
      
      return true;
    });
  }, [dataState.files, dataState.assignments, dataState.syncStatuses, uiState.filterOptions]);

  // Show loading state
  if (!moduleContext.initialized) {
    return (
      <div className={`kb-assignment-module ${className}`} data-testid={testId}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Initializing module context...</span>
        </div>
      </div>
    );
  }

  if (!moduleState.initialized || moduleState.loading) {
    return (
      <div className={`kb-assignment-module ${className}`} data-testid={testId}>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">Loading assignments...</span>
        </div>
      </div>
    );
  }

  // Show error state
  if (moduleState.error) {
    return (
      <div className={`kb-assignment-module ${className}`} data-testid={testId}>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Module Error</h3>
              <p className="text-red-600 dark:text-red-400">{moduleState.error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`kb-assignment-module ${className}`} data-testid={testId}>
      {/* Module header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              Knowledge Base Assignments
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 dark:text-gray-300 dark:text-gray-600">
              Assign knowledge base files to parrots for enhanced AI responses
            </p>
          </div>
          
          {/* View toggle */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setModuleState(prev => ({ ...prev, view: 'assignments' }))}
              className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                moduleState.view === 'assignments'
                  ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              Assignments
            </button>
            {config.enableAssignmentHistory && (
              <button
                onClick={() => setModuleState(prev => ({ ...prev, view: 'history' }))}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  moduleState.view === 'history'
                    ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                History
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      {moduleState.view === 'assignments' && (
        <>
          {/* Batch operations panel */}
          {config.allowBatchOperations && (
            <BatchOperationsPanel
              files={dataState.files}
              parrots={dataState.parrots}
              selectedFiles={uiState.selectedFiles}
              selectedParrots={uiState.selectedParrots}
              currentAssignments={dataState.assignments}
              onBatchAssignment={handleBatchAssignment}
              onFileSelection={handleFileSelection}
              onParrotSelection={handleParrotSelection}
              onConflictCheck={handleConflictCheck}
              showPreview={config.showBatchPreview !== false}
              className="mb-6"
            />
          )}

          {/* Assignment interface */}
          <AssignmentInterface
            files={filteredFiles}
            parrots={dataState.parrots}
            assignments={dataState.assignments}
            syncStatuses={dataState.syncStatuses}
            selectedFiles={uiState.selectedFiles}
            expandedFiles={uiState.expandedFiles}
            config={config}
            onAssignmentToggle={handleAssignmentToggle}
            onFileSelection={handleFileSelection}
            onFileExpansion={handleFileExpansion}
            getAssignmentStatus={getAssignmentStatus}
            getSyncStatus={getSyncStatus}
            filterOptions={uiState.filterOptions}
            onFilterChange={(filters) => setUiState(prev => ({ ...prev, filterOptions: filters }))}
          />

          {/* Sync status display */}
          {config.showSyncStatus && (
            <SyncStatusDisplay
              syncStatuses={dataState.syncStatuses}
              realTimeEnabled={config.enableRealTimeUpdates}
              className="mt-6"
            />
          )}
        </>
      )}

      {/* Assignment history panel */}
      {moduleState.view === 'history' && config.enableAssignmentHistory && (
        <AssignmentHistoryPanel
          config={config}
          moduleContext={moduleContext}
        />
      )}

      {/* Conflict resolution dialog */}
      {uiState.showConflictDialog && config.showConflictResolution && (
        <ConflictResolutionDialog
          conflicts={dataState.conflicts}
          onResolve={handleConflictResolution}
          onCancel={() => setUiState(prev => ({ ...prev, showConflictDialog: false }))}
        />
      )}
    </div>
  );
};

/**
 * Get default configuration for KnowledgeBaseAssignmentModule
 */
KnowledgeBaseAssignmentModule.getDefaultConfig = (context) => {
  return mergeWithDefaults({}, context);
};

export default KnowledgeBaseAssignmentModule;
